import { BookingEntity } from 'Models/Entities';
import { convertBookingToWizardDataUsingRecentSummary } from 'Util/_HumanWritten/BookingWizard/SummaryToWizardUtils';
import { normaliseSingleDriver } from 'Util/_HumanWritten/DriverAssignment';
import { isNotNullOrUndefined, isNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { convertWeight, parseWeightUnit } from 'Util/Weight';
import { appConfigStore } from 'Models/AppConfigStore';
import { BookingSummary } from 'Views/Components/_HumanWritten/BookingSummaryDto';
import {
	BookingWizardData,
	BookingWizardInnerDataType,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { BookingAddOn } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BookingAddOn';
import { PassengerDetail } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';
import { FreightBookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/FreightBookingWizardData';
import { PassengerBookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerBookingWizardData';
import { VehicleBookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/VehicleBookingWizardData';
import { getOldWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataStore';
import { BookingWizardCartFields } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardTypes';
import { SelectedTrips } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardWrap';

// Converts a bookingEntity to an instance of BookingWizardData to be used in the local storage
// primarily for editing bookings from the cart tab
export function ferryBookingToWizardData(
	bookingList: BookingEntity[],
	currentBooking?: BookingWizardData,
	selectedTrips?: SelectedTrips,
	alteration?: boolean,
	alterReturn: boolean = true,
): BookingWizardCartFields[] {
	let editBooking = false;
	const listOfBookings: BookingWizardCartFields[] = bookingList
		.sort((a: BookingEntity, b: BookingEntity) => {
			if (a.created > b.created) {
				return 1;
			}
			if (a.created < b.created) {
				return -1;
			}
			if (a.bookingSummaries.recentSummary.tripDateTime > b.bookingSummaries.recentSummary.tripDateTime) {
				return 1;
			}
			if (a.bookingSummaries.recentSummary.tripDateTime < b.bookingSummaries.recentSummary.tripDateTime) {
				return -1;
			}
			return 0;
		})
		.map(x => {
			if (x.alterations.length !== 0) {
				if (x.alterations[0]?.bookingId === currentBooking?.bookingToEdit) {
					editBooking = true;
					const result: BookingWizardCartFields = {
						wizardData: currentBooking,
						departingJourney: currentBooking.departingJourney,
						returningJourney: currentBooking.returningJourney,
					};
					return result;
				}
			}
			// This is to determine the return booking info as a booking can
			// either have a returnBooking or be the returnBookingFor another booking.
			// NOTE: There won't be an associated returnBooking for a one-way trip
			// eslint-disable-next-line no-nested-ternary
			let returnBookingInfo: BookingEntity | null = null;
			if (alterReturn) {
				returnBookingInfo = x.returnBooking ? x.returnBooking : x.returnBookingFor;
			}
			let bookingSummaryInfo = x.bookingSummaries.recentSummary;

			// Determine what the departure booking is and get the associated return trip
			if (alteration && alterReturn) {
				// eslint-disable-next-line no-nested-ternary
				returnBookingInfo = x.returnBooking ? x.returnBooking : x.returnBookingFor !== null ? x : null;
				bookingSummaryInfo = x.returnBooking
					? x.bookingSummaries.recentSummary
					: (x.returnBookingFor?.bookingSummaries.recentSummary ?? x.bookingSummaries.recentSummary);
			}

			return {
				wizardData: convertBookingToWizardDataUsingRecentSummary(x, alteration, alterReturn),
				// Add departingJourney if departing trip is available
				...(bookingSummaryInfo && {
					departingJourney: {
						tripId: bookingSummaryInfo.ferryTrip.id,
						startStopId: bookingSummaryInfo.startStopId,
						endStopId: bookingSummaryInfo.endStopId,
					},
				}),
				// Add returningJourney if returning trip is available
				...(returnBookingInfo &&
					returnBookingInfo.bookingSummaries.recentSummary && {
						returningJourney: {
							tripId: returnBookingInfo.bookingSummaries.recentSummary.ferryTrip.id,
							startStopId: returnBookingInfo.bookingSummaries.recentSummary.startStopId,
							endStopId: returnBookingInfo.bookingSummaries.recentSummary.endStopId,
						},
					}),
			};
		});
	if (!!currentBooking && !!selectedTrips && !editBooking) {
		listOfBookings.push({
			wizardData: currentBooking,
			...(currentBooking.departingJourney && {
				departingJourney: currentBooking.departingJourney,
			}),
			...(currentBooking.returningJourney && {
				returningJourney: currentBooking.returningJourney,
			}),
		});
	}
	return listOfBookings;
}

/**
 * Normalises the dynamic-field values stored on the alteration context into the
 * `Record<string, string>` shape the booking wizard works with, so editing a
 * booking pre-fills the customer's previously entered values. The backend
 * serialises them as a `[{ fieldId, value }]` list; an object map is also handled
 * defensively.
 */
function getDynamicFieldValues(bookingSummary: BookingSummary): Record<string, string> {
	const raw = (bookingSummary.alterationContext as any)?.dynamicFieldValues;
	if (!raw) return {};
	if (Array.isArray(raw)) {
		const values: Record<string, string> = {};
		raw.forEach((entry: any) => {
			if (entry && entry.fieldId) {
				values[String(entry.fieldId)] = String(entry.value ?? '');
			}
		});
		return values;
	}
	if (typeof raw === 'object') {
		return Object.entries(raw).reduce<Record<string, string>>((acc, [key, value]) => {
			acc[key] = String(value ?? '');
			return acc;
		}, {});
	}
	return {};
}

/**
 * Reads the returning leg's fare type and seat off its own summary, keyed by passenger.
 *
 * <p>A return booking is two bookings, each with its own alteration, and each summary describes only
 * the leg it belongs to — so both legs' seating arrives in the <b>departing</b> fields of a different
 * summary. The wizard payload is the other shape: one row per passenger carrying both legs, because
 * one passenger row backs both (see <c>SeatAssignmentEntity</c>). Reopening a booking read the
 * departing summary alone, so the returning fare type and seat came back empty on every edit.</p>
 *
 * <p>Keyed on the passenger id, which is the same row on both legs and is what the server matches
 * the payload back to.</p>
 */
function returningSeatingByPassengerId(returnSummary: BookingSummary | null): Map<string, PassengerDetail> {
	const byId = new Map<string, PassengerDetail>();
	if (isNullOrUndefined(returnSummary)) {
		return byId;
	}

	[
		returnSummary.adultPassengerTickets,
		returnSummary.childPassengerTickets,
		returnSummary.infantPassengerTickets,
		returnSummary.passengerDTickets,
		returnSummary.passengerETickets,
		returnSummary.passengerFTickets,
		returnSummary.passengerGTickets,
		returnSummary.passengerHTickets,
	].forEach(tickets => (tickets ?? []).forEach(ticket => {
		if (ticket?.id) {
			byId.set(ticket.id, ticket);
		}
	}));

	return byId;
}

/**
 * Folds that seating onto one passenger-type slot of the departing summary.
 *
 * <p>Left alone where the returning leg has nothing to say about a passenger — somebody added to the
 * outbound leg only, or a leg with no fare types at all. Undefined rather than a blank string on a
 * missing seat, because the wizard reads "no seat" from absence.</p>
 */
function withReturningSeating(
	tickets: PassengerDetail[] | undefined,
	returning: Map<string, PassengerDetail>,
): PassengerDetail[] | undefined {
	if (!tickets || returning.size === 0) {
		return tickets;
	}

	return tickets.map(ticket => {
		const homeward = returning.get(ticket.id);
		if (!homeward) {
			return ticket;
		}
		return {
			...ticket,
			returningPassengerClassId: homeward.passengerClassId,
			returningPassengerClassName: homeward.passengerClassName,
			returningSeatId: homeward.seatId,
			returningSeatLabel: homeward.seatLabel,
			returningSeatAutoAssigned: homeward.seatAutoAssigned,
		};
	});
}

function savedExactWeightInDisplayUnit(
	info: { exactWeight?: number | null; exactWeightUnit?: string | null } | null | undefined,
): number | null {
	if (info?.exactWeight == null) return null;
	const displayUnit = appConfigStore.weightDisplayUnit;
	return convertWeight(Number(info.exactWeight), parseWeightUnit(info.exactWeightUnit, displayUnit), displayUnit);
}

/**
 * The "name on booking" an edit starts from.
 *
 * The summary's primary name is the flagged passenger's — but with nobody flagged it falls back to
 * the account holder, which for a corporate customer is the company. Seeding that showed the company
 * in the name fields of a booking that had a passenger's name all along, and saving it put the company
 * on the booking (#2834). With nobody flagged, the passenger the save will flag — the first adult,
 * else the first passenger — speaks for the booking wherever it has a name.
 */
function seededPrimaryName(bookingSummary: BookingSummary): { firstName: string; lastName: string } {
	const tickets = [
		...(bookingSummary.adultPassengerTickets ?? []),
		...(bookingSummary.childPassengerTickets ?? []),
		...(bookingSummary.infantPassengerTickets ?? []),
		...(bookingSummary.passengerDTickets ?? []),
		...(bookingSummary.passengerETickets ?? []),
		...(bookingSummary.passengerFTickets ?? []),
		...(bookingSummary.passengerGTickets ?? []),
		...(bookingSummary.passengerHTickets ?? []),
	];
	const summaryName = {
		firstName: bookingSummary.primaryFirstName ?? '',
		lastName: bookingSummary.primaryLastName ?? '',
	};
	if (tickets.some(ticket => ticket.isPrimaryContact)) return summaryName;

	const candidate = bookingSummary.adultPassengerTickets?.[0] ?? tickets[0];
	return {
		firstName: stringNotEmpty(candidate?.firstName?.trim()) ? candidate.firstName : summaryName.firstName,
		lastName: stringNotEmpty(candidate?.lastName?.trim()) ? candidate.lastName : summaryName.lastName,
	};
}

export function getInnerWizardData(
	bookingSummary: BookingSummary,
	alterReturn: boolean,
	alteration: boolean,
	returnSummary: BookingSummary | null,
): BookingWizardInnerDataType {
	const oldWizardData = getOldWizardData();

	/*
		Only where the return leg is part of this edit. `alterReturn` false means the wizard is
		working on one leg alone — the caller has already made that leg the departing one, and folding
		a returning fare type onto a payload with no returning ticket would seat a sailing the
		submission does not carry.
	*/
	const returning = alterReturn ? returningSeatingByPassengerId(returnSummary) : new Map<string, PassengerDetail>();
	const withReturn = (tickets: PassengerDetail[] | undefined) => withReturningSeating(tickets, returning);

	if (bookingSummary.cargoInfo !== null) {
		const vehicleExactWeight = savedExactWeightInDisplayUnit(bookingSummary.cargoInfo);
		const trailerExactWeight = savedExactWeightInDisplayUnit(bookingSummary.towOnInfo);
		const vehicleWizardData = new VehicleBookingWizardData({
			type: 'vehicle',
			adultTickets: withReturn(bookingSummary.adultPassengerTickets),
			childTickets: withReturn(bookingSummary.childPassengerTickets),
			infantTickets: withReturn(bookingSummary.infantPassengerTickets),
			passengerDTickets: withReturn(bookingSummary.passengerDTickets),
			passengerETickets: withReturn(bookingSummary.passengerETickets),
			passengerFTickets: withReturn(bookingSummary.passengerFTickets),
			passengerGTickets: withReturn(bookingSummary.passengerGTickets),
			passengerHTickets: withReturn(bookingSummary.passengerHTickets),

			note: bookingSummary.note,
			dynamicFieldValues: getDynamicFieldValues(bookingSummary),

			trailerCheckboxSelected: bookingSummary.towOnInfo !== null,

			driverFirstName: bookingSummary.driverFirstName ?? '',
			driverLastName: bookingSummary.driverLastName ?? '',
			driverPhone: bookingSummary.driverPhone ?? '',
			cargoCharacteristics: bookingSummary.alterationContext?.cargoCharacteristics ?? [],
			removedInheritedCharacteristics: bookingSummary.alterationContext?.removedInheritedCharacteristics ?? [],
			cargoIdentification: bookingSummary.cargoInfo ? bookingSummary.cargoInfo.cargoIdentification : '',
			cargoTypeId: bookingSummary.cargoInfo ? bookingSummary.cargoInfo.cargoTypeId : '',
			cargoMake: bookingSummary.cargoInfo ? bookingSummary.cargoInfo.cargoType.cargoMake : '',
			cargoModel: bookingSummary.cargoInfo ? bookingSummary.cargoInfo.cargoType.cargoModel : '',
			vehicleLengthId: bookingSummary.cargoInfo ? bookingSummary.cargoInfo.selectedLengthId : '',
			vehicleWeightId: bookingSummary.cargoInfo ? bookingSummary.cargoInfo.selectedWeightId : '',
			vehicleWidthId: bookingSummary.cargoInfo ? bookingSummary.cargoInfo.selectedWidthId : '',
			// The input renders in the display unit, and its prefill effect overwrites the value
			// with the type's default unless it is flagged as user-edited — a saved weight counts.
			vehicleExactWeight,
			vehicleExactWeightUnit: vehicleExactWeight !== null ? appConfigStore.weightDisplayUnit : null,
			vehicleExactWeightUserEdited: vehicleExactWeight !== null,
			showWidthDetails: bookingSummary.cargoInfo ? !!bookingSummary.cargoInfo.selectedWidthId : false,
			hiredVehicle: bookingSummary.cargoInfo ? bookingSummary.cargoInfo.tbc : false,
			// eslint-disable-next-line no-nested-ternary
			trailerLengthId: bookingSummary.towOnInfo
				? bookingSummary.towOnInfo.selectedLengthId
				: alteration
					? (oldWizardData?.innerWizardData as VehicleBookingWizardData)?.trailerLengthId
					: '',
			trailerTypeId: bookingSummary.towOnInfo?.towOnTypeId ?? 'NO_TRAILER',
			trailerWeightId: bookingSummary.towOnInfo?.selectedWeightId ?? '',
			trailerExactWeight,
			trailerExactWeightUnit: trailerExactWeight !== null ? appConfigStore.weightDisplayUnit : null,
			trailerExactWeightUserEdited: trailerExactWeight !== null,
			trailerWidthId: bookingSummary.towOnInfo?.selectedWidthId ?? '',
			showTrailerWidthDetails: !!bookingSummary.towOnInfo?.selectedWidthId,
			trailerCargoCharacteristics: bookingSummary.alterationContext?.trailerCargoCharacteristics ?? [],
			departingTripOptions: (bookingSummary.additionalBookingOptions ?? []).map(item => {
				return new BookingAddOn({
					optionId: item.option?.id ?? '',
					amount: item.quantity ?? 0,
					optionPrice: item.option?.staticPrice ?? 0,
					optionName: item.option?.name ?? '',
					gstExempt: item.option?.excludeGST,
				});
			}),
			returningTripOptions:
				isNotNullOrUndefined(returnSummary) && alterReturn
					? (returnSummary.additionalBookingOptions ?? []).map(item => {
							return new BookingAddOn({
								optionId: item.option?.id ?? '',
								amount: item.quantity ?? 0,
								optionPrice: item.option?.staticPrice ?? 0,
								optionName: item.option?.name ?? '',
								gstExempt: item.option?.excludeGST,
							});
						})
					: [],
		});

		// Both `wizardData` and `oldWizardData` are built from here, so normalising a booking that
		// was saved with two drivers happens on both sides at once — the counters show one driver
		// and the user can move it, and the reservation step does not read the repair itself as an
		// edit.
		normaliseSingleDriver(vehicleWizardData);
		return vehicleWizardData;
	}
	if (isNotNullOrUndefined(bookingSummary.freightDetail)) {
		return new FreightBookingWizardData({
			type: 'freight',
			freightCategoryId: bookingSummary.freightDetail.freightCategoryId,
			freightTypeId: bookingSummary.freightDetail.freightTypeId,
			quantity: bookingSummary.freightDetail.quantity,
			weight: bookingSummary.freightDetail.weight,
			width: bookingSummary.freightDetail.width,
			height: bookingSummary.freightDetail.height,
			length: bookingSummary.freightDetail.length,
			handlingIds: bookingSummary.freightDetail.freightHandlings.map(x => x.freightHandlingId),
			description: bookingSummary.freightDetail.description,
			receiverFirstName: bookingSummary.freightDetail?.freightReceiverContact?.firstName ?? '',
			receiverLastName: bookingSummary.freightDetail?.freightReceiverContact?.lastName ?? '',
			receiverPhone: bookingSummary.freightDetail?.freightReceiverContact?.phone ?? '',
			receiverEmail: bookingSummary.freightDetail?.freightReceiverContact?.email ?? '',
			receiverCompanyName: bookingSummary.freightDetail?.freightReceiverContact?.companyName ?? '',
			notifyFirstName: bookingSummary.freightDetail?.freightNotifyContact?.firstName ?? '',
			notifyLastName: bookingSummary.freightDetail?.freightNotifyContact?.lastName ?? '',
			notifyPhone: bookingSummary.freightDetail?.freightNotifyContact?.phone ?? '',
			notifyEmail: bookingSummary.freightDetail?.freightNotifyContact?.email ?? '',
			notifyCompanyName: bookingSummary.freightDetail?.freightNotifyContact?.companyName ?? '',
			dynamicFieldValues: getDynamicFieldValues(bookingSummary),
			departingTripOptions: (bookingSummary.additionalBookingOptions ?? []).map(item => {
				return new BookingAddOn({
					optionId: item.option?.id ?? '',
					amount: item.quantity ?? 0,
					optionPrice: item.option?.staticPrice ?? 0,
					optionName: item.option?.name ?? '',
					gstExempt: item.option?.excludeGST,
				});
			}),
		});
	}
	const primaryName = seededPrimaryName(bookingSummary);
	const passengerWizardData = new PassengerBookingWizardData({
		type: 'passenger',
		adultTickets: withReturn(bookingSummary.adultPassengerTickets),
		childTickets: withReturn(bookingSummary.childPassengerTickets),
		infantTickets: withReturn(bookingSummary.infantPassengerTickets),
		passengerDTickets: withReturn(bookingSummary.passengerDTickets),
		passengerETickets: withReturn(bookingSummary.passengerETickets),
		passengerFTickets: withReturn(bookingSummary.passengerFTickets),
		passengerGTickets: withReturn(bookingSummary.passengerGTickets),
		passengerHTickets: withReturn(bookingSummary.passengerHTickets),
		// Seed the "name on booking" (primary contact) so editing a booking shows the
		// existing value instead of the BookingDetails tab pre-filling the account holder's
		// name — otherwise edits silently revert the booking name to the account holder.
		primaryFirstName: primaryName.firstName,
		primaryLastName: primaryName.lastName,
		note: bookingSummary.note,
		dynamicFieldValues: getDynamicFieldValues(bookingSummary),
		departingTripOptions: (bookingSummary.additionalBookingOptions ?? []).map(item => {
			return new BookingAddOn({
				optionId: item.option?.id ?? '',
				amount: item.quantity ?? 0,
				optionPrice: item.option?.staticPrice ?? 0,
				optionName: item.option?.name ?? '',
				gstExempt: item.option?.excludeGST,
			});
		}),
		returningTripOptions:
			isNotNullOrUndefined(returnSummary) && alterReturn
				? (returnSummary.additionalBookingOptions ?? []).map(item => {
						return new BookingAddOn({
							optionId: item.option?.id ?? '',
							amount: item.quantity ?? 0,
							optionPrice: item.option?.staticPrice ?? 0,
							optionName: item.option?.name ?? '',
							gstExempt: item.option?.excludeGST,
						});
					})
				: [],
	});

	// The passenger flow does not show a driver toggle, but the wizard carries these arrays across
	// a switch to the vehicle tab, so a booking with two drivers must not arrive there still
	// holding both.
	normaliseSingleDriver(passengerWizardData);
	return passengerWizardData;
}
